<script lang="ts" setup>
import { type PolisServiceCtClausule } from '@services/polis-public/orval-api/model/polisServiceCtClausule';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import { computed, toRefs } from 'vue';

import ClausuleDetails from '../ClausuleDetails.vue';
import { usePolisDetails } from '../usePolisDetails';

type VerzekeringMetClausules = {
  clausule?: PolisServiceCtClausule[] | null;
  dekking?: Array<{ clausule?: PolisServiceCtClausule[] | null }> | null;
};

const props = defineProps<{
  polisnummer: string;
}>();

const { polisnummer } = toRefs(props);
const { data } = usePolisDetails(polisnummer);

const subpolis = computed(() =>
  data?.value?.polis?.subpolis?.length ? data.value.polis.subpolis[0] : undefined,
);

const verzekering = computed<VerzekeringMetClausules | undefined>(() => {
  if (!subpolis.value || !data.value?.polis?.soortverzekering) return undefined;

  const verzekeringen = subpolis.value as unknown as Record<
    string,
    VerzekeringMetClausules[] | undefined
  >;

  return verzekeringen[data.value.polis.soortverzekering.toLowerCase()]?.[0];
});

const clausules = computed<PolisServiceCtClausule[]>(() => [
  ...(subpolis.value?.clausule ?? []),
  ...(verzekering.value?.clausule ?? []),
  ...(verzekering.value?.dekking?.flatMap((d) => d.clausule ?? []) ?? []),
]);

const uniekeClausules = computed(
  () =>
    new Set(
      clausules.value
        .map((clausule) => clausule.internclausulenummer)
        .filter((nummer): nummer is string => !!nummer)
        .map(Number)
        .filter(Number.isFinite),
    ),
);
</script>
<template>
  <div class="flex flex-col gap-4">
    <VInfoBox type="info" v-if="!clausules.length"> Deze polis heeft geen clausules. </VInfoBox>
    <template v-else>
      <div v-for="clausule in uniekeClausules" :key="clausule">
        <ClausuleDetails :clausulenummer="clausule" :polisnummer="polisnummer" />
      </div>
    </template>
  </div>
</template>
